import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const LiquidImage = ({ imageSrc, className, autoAnimate = false }) => {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const animationIdRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        // Scene setup
        const container = containerRef.current;
        const width = container.offsetWidth;
        const height = container.offsetHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true
        });
        renderer.setClearColor(0x000000, 0);
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        container.appendChild(renderer.domElement);

        // Load texture (support both images and videos)
        const isVideo = imageSrc.endsWith('.mp4') || imageSrc.endsWith('.webm');
        let texture;
        let videoElement = null;

        const updatePlaneSize = (textureWidth, textureHeight) => {
            if (!imageMesh || !deformationMesh) return;

            const imageAspect = textureWidth / textureHeight;
            const containerAspect = width / height;

            let scaleX = 1;
            let scaleY = 1;

            if (containerAspect > imageAspect) {
                // Container is wider than image aspect
                scaleX = imageAspect / containerAspect;
            } else {
                // Container is taller than image aspect
                scaleY = containerAspect / imageAspect;
            }

            imageMesh.scale.set(scaleX, scaleY, 1);
            deformationMesh.scale.set(scaleX, scaleY, 1);
        };

        if (isVideo) {
            // Create video element
            const video = document.createElement('video');
            video.src = imageSrc;
            video.crossOrigin = 'anonymous';
            video.loop = true;
            video.muted = true;
            video.playsInline = true;
            video.autoplay = true;

            // Store reference for cleanup
            videoElement = video;

            // Ensure video plays and update sizes
            video.addEventListener('loadedmetadata', () => {
                updatePlaneSize(video.videoWidth, video.videoHeight);
                video.play().catch(err => {
                    console.log('Video autoplay failed:', err);
                });
            });

            // Create video texture
            texture = new THREE.VideoTexture(video);
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
        } else {
            // Load image texture
            const textureLoader = new THREE.TextureLoader();
            texture = textureLoader.load(imageSrc, (t) => {
                updatePlaneSize(t.image.width, t.image.height);
            });
            texture.minFilter = THREE.LinearFilter;
            texture.magFilter = THREE.LinearFilter;
        }

        // Create render targets for FBO (double buffering)
        const renderTarget1 = new THREE.WebGLRenderTarget(width, height, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
        });

        const renderTarget2 = new THREE.WebGLRenderTarget(width, height, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
        });

        // Deformation shader (FBO pass)
        const deformationVertexShader = `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        const deformationFragmentShader = `
            precision highp float;
            uniform sampler2D tLast;
            uniform vec2 uMouse;
            uniform float uMouseSpeed;
            uniform float uIntensity;
            uniform float uRadius;
            uniform float uHasLastTexture;
            uniform float uIsMouseInCanvas;

            varying vec2 vUv;

            #define SPEED_FACTOR 0.02
            #define MAX_SPEED 0.003
            #define RESOLUTION 0.002

            void main() {
                if (uIsMouseInCanvas < 0.5) {
                    gl_FragColor = vec4(0.5, 0.5, 0.0, 1.0);
                    return;
                }

                vec2 mouseUv = uMouse;
                float dist = length(vUv - mouseUv);
                
                // Brush logic based on mouse speed
                float speed = smoothstep(0.0, MAX_SPEED, uMouseSpeed) * SPEED_FACTOR * uIntensity;
                float circle = smoothstep(uRadius + speed, 0.0, dist) * 0.05;
                float color = circle;

                // Remanence/Diffusion: Sample neighbors from previous frame
                if (uHasLastTexture > 0.5) {
                    float remanence = 0.0;
                    remanence += texture2D(tLast, vUv + vec2(RESOLUTION, 0.0)).r * 0.25;
                    remanence += texture2D(tLast, vUv + vec2(-RESOLUTION, 0.0)).r * 0.25;
                    remanence += texture2D(tLast, vUv + vec2(0.0, RESOLUTION)).r * 0.25;
                    remanence += texture2D(tLast, vUv + vec2(0.0, -RESOLUTION)).r * 0.25;
                    color += remanence * 0.99;
                }
                
                color -= 0.002;
                color = max(color, 0.5);
                gl_FragColor = vec4(vec3(color), 1.0);
            }
        `;

        // Image displacement shader (final pass)
        const imageVertexShader = `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        const imageFragmentShader = `
            uniform sampler2D uDeformationTexture;
            uniform float uDeformationIntensity;
            uniform sampler2D uTexture;
            uniform float uTime;
            uniform float uTransitionActive;
            varying vec2 vUv;

            void main() {
                vec2 uv = vUv;
                
                // Global wave-based displacement (covers entire image)
                if (uTransitionActive > 0.5) {
                    float t = uTime;
                    float intensity = uDeformationIntensity;
                    
                    // Primary waves - horizontal and vertical
                    float waveX = sin(uv.y * 18.0 - t * 14.0) * 0.12;
                    float waveY = cos(uv.x * 16.0 + t * 11.0) * 0.10;
                    uv += vec2(waveX, waveY) * intensity;
                    
                    // Diagonal movement
                    float diag = sin((uv.x + uv.y) * 20.0 - t * 13.0) * 0.08;
                    uv += vec2(diag, diag * 0.8) * intensity;
                    
                    // High-frequency noise for organic look
                    float noise1 = sin(uv.x * 28.0 + t * 10.0) * cos(uv.y * 22.0 + t * 8.0);
                    float noise2 = sin(uv.x * 18.0 + t * 6.0) * cos(uv.y * 16.0 + t * 7.0);
                    uv += vec2(noise1, noise2) * 0.08 * intensity;
                }

                vec4 finalColor = texture2D(uTexture, uv);
                gl_FragColor = finalColor;
            }
        `;

        // Create materials
        const deformationMaterial = new THREE.ShaderMaterial({
            vertexShader: deformationVertexShader,
            fragmentShader: deformationFragmentShader,
            uniforms: {
                tLast: { value: null },
                uMouse: { value: new THREE.Vector2(0.5, 0.5) },
                uMouseSpeed: { value: 0 },
                uIntensity: { value: 2.0 },
                uRadius: { value: 0.2 },
                uHasLastTexture: { value: 0 },
                uIsMouseInCanvas: { value: 0 }
            }
        });

        const imageMaterial = new THREE.ShaderMaterial({
            vertexShader: imageVertexShader,
            fragmentShader: imageFragmentShader,
            transparent: true,
            uniforms: {
                uDeformationTexture: { value: null },
                uDeformationIntensity: { value: 0.2 },
                uTexture: { value: texture },
                uTime: { value: 0 },
                uTransitionActive: { value: 0 }
            }
        });

        // Create plane geometry
        const geometry = new THREE.PlaneGeometry(2, 2);

        // Scene for deformation pass
        const deformationScene = new THREE.Scene();
        const deformationMesh = new THREE.Mesh(geometry, deformationMaterial);
        deformationScene.add(deformationMesh);

        // Scene for final image
        const imageMesh = new THREE.Mesh(geometry, imageMaterial);
        scene.add(imageMesh);

        // Mouse tracking
        const mouse = {
            current: new THREE.Vector2(0.5, 0.5),
            previous: new THREE.Vector2(0.5, 0.5),
            speed: 0,
            isInCanvas: false
        };

        const handleMouseMove = (e) => {
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Convert to UV coordinates (0 to 1)
            mouse.previous.copy(mouse.current);
            mouse.current.x = x / rect.width;
            mouse.current.y = 1.0 - (y / rect.height); // Flip Y for WebGL
            mouse.isInCanvas = true;
        };

        const handleMouseEnter = () => {
            mouse.isInCanvas = true;
        };

        const handleMouseLeave = () => {
            mouse.isInCanvas = false;
        };

        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseenter', handleMouseEnter);
        container.addEventListener('mouseleave', handleMouseLeave);

        // Auto-animation when autoAnimate prop is true
        let autoAnimationFrame;
        let autoAnimationTime = 0;

        const autoAnimateDistortion = () => {
            if (!autoAnimate) return;

            autoAnimationTime += 0.08;

            // Spiral pattern that covers the entire image
            const centerX = 0.5;
            const centerY = 0.5;
            const expandingRadius = 0.4 + Math.sin(autoAnimationTime * 0.5) * 0.2;
            const angle = autoAnimationTime * 3;

            mouse.previous.copy(mouse.current);
            mouse.current.x = centerX + Math.cos(angle) * expandingRadius;
            mouse.current.y = centerY + Math.sin(angle) * expandingRadius;
            mouse.isInCanvas = true;

            // Very high speed for strong, visible effect
            const deltaX = mouse.current.x - mouse.previous.x;
            const deltaY = mouse.current.y - mouse.previous.y;
            mouse.speed = Math.sqrt(deltaX * deltaX + deltaY * deltaY) * 4;

            autoAnimationFrame = requestAnimationFrame(autoAnimateDistortion);
        };

        if (autoAnimate) {
            autoAnimateDistortion();
        }

        // Animation loop
        let startTime = Date.now();

        const animate = () => {
            const elapsed = (Date.now() - startTime) / 1000;

            // Update time uniform for wave animation
            imageMaterial.uniforms.uTime.value = elapsed;

            // Set transition active based on autoAnimate prop
            imageMaterial.uniforms.uTransitionActive.value = autoAnimate ? 1 : 0;

            // Render final image to screen
            renderer.setRenderTarget(null);
            renderer.render(scene, camera);

            animationIdRef.current = requestAnimationFrame(animate);
        };

        animate();

        // Cleanup
        sceneRef.current = { renderer, container };

        return () => {
            if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseenter', handleMouseEnter);
            container.removeEventListener('mouseleave', handleMouseLeave);

            if (renderer) {
                renderer.dispose();
                // Safety check: only remove if it's still a child
                if (renderer.domElement && container.contains(renderer.domElement)) {
                    container.removeChild(renderer.domElement);
                }
            }

            renderTarget1.dispose();
            renderTarget2.dispose();
            geometry.dispose();
            deformationMaterial.dispose();
            imageMaterial.dispose();
            texture.dispose();
        };
    }, [imageSrc, autoAnimate]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ width: '100%', height: '100%', overflow: 'hidden' }}
        />
    );
};

export default LiquidImage;
